// /meta — shows the current Standard archetype leaderboard from the
// ingested exemplar corpus. Same view the agent sees when calling
// `get_meta_snapshot`. Useful for:
//   • Eyeballing what's actually tournament-tier right now
//   • Picking an archetype before typing a build-deck brief
//   • Sanity-checking agent behavior (if the agent picked a mono-red shell
//     and that doesn't appear here, the prompt rule 12a is being ignored)

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Trophy, ExternalLink } from "lucide-react";
import { getMeta, type MetaArchetype, type MetaSnapshot, type MetaWindow } from "../lib/api";
import { cn } from "../lib/cn";

// const WINDOW_OPTIONS: Array<{ value: MetaWindow; label: string }> = [
//   { value: 7, label: "Last 7 days" },
//   { value: 30, label: "Last 30 days" },
//   { value: 60, label: "Last 60 days" },
//   { value: 90, label: "Last 90 days" },
//   { value: "all", label: "All time" },
// ];

export default function MetaSnapshotPage() {
  // The setter is unused while the picker is hidden, but the state
  // hook needs to stay so the cache key + getMeta() arg can keep
  // sourcing the active window. Re-enable the picker by uncommenting
  // the JSX block below and renaming this back to setWindowDays.
  const [windowDays] = useState<MetaWindow>(30);
  const { data, isLoading, error } = useQuery({
    queryKey: ["meta", 15, 1.3, windowDays],
    queryFn: () => getMeta(15, 1.3, windowDays),
  });

  return (
    <div className="mx-auto max-w-5xl px-6 md:px-10 py-10 space-y-8">
      <header>
        <div className="eyebrow">Meta</div>
        <h1 className="mt-2 font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.0]">
          The current meta.
          <br />
          <span className="foil">As the corpus sees it.</span>
        </h1>
        <p className="text-sm text-fg-muted mt-5 max-w-2xl leading-relaxed">
          Aggregated from the ingested deck corpus. Tournament counts include
          only MTGO + mtgtop8 + mtggoldfish (tier ≥ 1.3); totals include
          community platforms too. The agent sees this same data via the{" "}
          <code className="text-xs font-mono text-fg-muted bg-bg-raised px-1 py-0.5 rounded">
            get_meta_snapshot
          </code>{" "}
          tool on open-ended briefs.
        </p>
      </header>

      {isLoading && (
        <div className="grid gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="skeleton h-16 w-full" />
          ))}
        </div>
      )}
      {error && (
        <div className="surface p-4 text-sm text-danger">
          {(error as Error).message}
        </div>
      )}

      {/* WindowPicker hidden — see comment above its definition. */}

      {data && (
        <>
          <Summary snap={data} />
          <Leaderboard archetypes={data.archetypes} />
          <SourcesPanel sources={data.sources} />
        </>
      )}
    </div>
  );
}

// WindowPicker is intentionally kept (commented out) here so re-
// enabling the meta time-range filter is a single-edit operation
// once the ingest backfill ships enough historical data to make
// the windows produce different results.
//
// function WindowPicker({
//   value,
//   onChange,
//   disabled,
// }: {
//   value: MetaWindow;
//   onChange: (v: MetaWindow) => void;
//   disabled?: boolean;
// }) {
//   return (
//     <div className="flex flex-wrap items-center gap-2 text-xs">
//       <span className="text-fg-muted">Window:</span>
//       <div className="inline-flex rounded-md border border-border overflow-hidden">
//         {WINDOW_OPTIONS.map((opt) => {
//           const active = opt.value === value;
//           return (
//             <button
//               key={String(opt.value)}
//               type="button"
//               disabled={disabled}
//               onClick={() => onChange(opt.value)}
//               className={cn(
//                 "px-2.5 py-1 transition-colors capitalize",
//                 active
//                   ? "bg-accent/20 text-accent"
//                   : "text-fg-muted hover:bg-bg-raised",
//                 disabled && "opacity-50 cursor-not-allowed",
//               )}
//             >
//               {opt.label}
//             </button>
//           );
//         })}
//       </div>
//     </div>
//   );
// }

function Summary({ snap }: { snap: MetaSnapshot }) {
  const windowSuffix =
    snap.window_days === null
      ? "all time"
      : `last ${snap.window_days}d`;
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      <StatCard
        label={`Total decks · ${windowSuffix}`}
        value={snap.total_decks.toString()}
      />
      <StatCard
        label={`Tournament-tier (≥ ${snap.min_tier_threshold})`}
        value={snap.tournament_decks.toString()}
        highlight
      />
      <StatCard
        label="Archetypes shown"
        value={snap.archetypes.length.toString()}
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="surface p-4">
      <div className="text-[11px] uppercase tracking-wider text-fg-subtle">
        {label}
      </div>
      <div
        className={cn(
          "mt-1 font-mono text-2xl tabular-nums tracking-tight",
          highlight ? "text-accent" : "text-fg",
        )}
      >
        {value}
      </div>
    </div>
  );
}

function Leaderboard({ archetypes }: { archetypes: MetaArchetype[] }) {
  // Use the largest count across the top N as the bar denominator so the
  // visual ranks are comparable. Falls back to 1 if the list is empty.
  const maxCount = Math.max(
    1,
    ...archetypes.map((a) => Math.max(a.tournament_count, a.total_count)),
  );
  return (
    <section className="space-y-3">
      <header className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
          Archetype leaderboard
        </h2>
        <span className="text-[11px] text-fg-subtle">
          Ranked by tournament count, then total
        </span>
      </header>
      <div className="surface p-4 space-y-2">
        {archetypes.map((a, idx) => (
          <ArchetypeRow key={a.archetype} rank={idx + 1} archetype={a} maxCount={maxCount} />
        ))}
        {archetypes.length === 0 && (
          <div className="text-xs text-fg-muted">
            No archetypes match the current threshold.
          </div>
        )}
      </div>
    </section>
  );
}

function ArchetypeRow({
  rank,
  archetype,
  maxCount,
}: {
  rank: number;
  archetype: MetaArchetype;
  maxCount: number;
}) {
  const tournamentWidth = `${(archetype.tournament_count / maxCount) * 100}%`;
  const totalWidth = `${(archetype.total_count / maxCount) * 100}%`;
  const colors = archetype.top_color_identity || "?";
  // Pre-tournament-count decks show a subtler bar; gives a clear "this is
  // a community-only archetype" visual even before reading the number.
  const hasTournamentSignal = archetype.tournament_count > 0;

  return (
    <Link
      to={`/?brief=${encodeURIComponent(`Build the best ${archetype.archetype} deck in current Standard`)}`}
      className="block rounded-md hover:bg-bg-raised/60 transition-colors px-2 py-2"
      title="Open Build Deck with this archetype pre-filled"
    >
      <div className="grid grid-cols-[2rem_1fr_auto] items-center gap-3">
        <div className="font-mono text-xs text-fg-subtle tabular-nums">
          {rank.toString().padStart(2, "0")}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ManaSigil colors={colors} />
            <div className="text-sm font-medium truncate">
              {archetype.archetype}
            </div>
            {hasTournamentSignal && archetype.max_tier_weight >= 1.5 && (
              <Trophy size={11} strokeWidth={2} className="text-accent shrink-0" />
            )}
          </div>
          <div className="mt-1.5 space-y-1">
            {/* Total bar — underlay */}
            <div className="relative h-1 rounded-full bg-bg-raised overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-fg-subtle/30 rounded-full"
                style={{ width: totalWidth }}
              />
              {/* Tournament bar — overlay, stronger color */}
              <div
                className={cn(
                  "absolute inset-y-0 left-0 rounded-full",
                  hasTournamentSignal ? "bg-accent/60" : "bg-transparent",
                )}
                style={{ width: tournamentWidth }}
              />
            </div>
          </div>
        </div>
        <div className="text-right font-mono text-xs tabular-nums shrink-0">
          <div
            className={cn(
              hasTournamentSignal ? "text-accent" : "text-fg-subtle",
            )}
          >
            {archetype.tournament_count} tier
          </div>
          <div className="text-fg-subtle text-[11px]">
            {archetype.total_count} total
          </div>
        </div>
      </div>
      {archetype.sample_source_urls.length > 0 && (
        <div className="mt-1.5 pl-[2.75rem] flex flex-wrap gap-2 text-[11px] text-fg-subtle">
          {archetype.sample_source_urls.slice(0, 3).map((u, i) => (
            <a
              key={u}
              href={u}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 hover:text-accent transition-colors"
            >
              sample #{i + 1}
              <ExternalLink size={9} strokeWidth={2} />
            </a>
          ))}
        </div>
      )}
    </Link>
  );
}

function ManaSigil({ colors }: { colors: string }) {
  // Tiny colored dots representing W/U/B/R/G. Keeps the list scannable by
  // color identity without importing full mana-pip art.
  const palette: Record<string, string> = {
    W: "bg-[#f7f6d6] border-[#dad7a6]",
    U: "bg-[#b3cfe6] border-[#7fa7cd]",
    B: "bg-[#7c7976] border-[#4a4946]",
    R: "bg-[#e09a8e] border-[#bd685a]",
    G: "bg-[#a1c2a1] border-[#6a9a6a]",
  };
  const pieces = colors.split("").filter((c) => palette[c]);
  if (pieces.length === 0) {
    return (
      <span className="inline-block w-2.5 h-2.5 rounded-full bg-fg-subtle/20 border border-border" />
    );
  }
  return (
    <span className="inline-flex gap-0.5 shrink-0" aria-label={`colors ${colors}`}>
      {pieces.map((c) => (
        <span
          key={c}
          className={cn("inline-block w-2.5 h-2.5 rounded-full border", palette[c])}
        />
      ))}
    </span>
  );
}

function SourcesPanel({ sources }: { sources: MetaSnapshot["sources"] }) {
  return (
    <section className="space-y-3">
      <header>
        <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
          Corpus sources
        </h2>
      </header>
      <div className="surface p-4">
        <ul className="space-y-1.5">
          {sources.map((s) => (
            <li
              key={s.source}
              className="grid grid-cols-[7rem_4rem_1fr_auto] items-baseline gap-3 text-xs font-mono tabular-nums"
            >
              <span className="text-fg">{s.source}</span>
              <span className="text-fg-subtle">tier {s.tier_weight.toFixed(1)}</span>
              <span className="text-fg-muted">{s.count.toLocaleString()} decks</span>
              <span className="text-fg-subtle whitespace-nowrap">
                {s.most_recent ? s.most_recent.slice(0, 10) : "—"}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
